import {
  WmModal,
  WmModalFooter,
  WmModalHeader,
  WmTagInput,
  WmTagOption
} from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import { ModalData } from "./AcademicsAndOffices";
import { useEffect, useReducer } from "react";
import { sendRequest, sendRequestWithPayload } from "@/common/http";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import { People } from "@/features/common/interfaces/Interface";
interface ShareAccessModalProps {
  toggleShareAccessModal: (modalData?: ModalData) => void;
  updatePeopleWithAccess: (
    uuids: Set<string>,
    type: string,
    nodeUuid: string
  ) => void;
  showShareAccessModal: boolean;
  availablePeople: People[] | null;
  modalData: ModalData;
}
function getFullName(user: People) {
  return `${user.givenName} ${user.middleName ? user.middleName : ""} ${
    user.familyName
  }`;
}

interface State {
  selectedPeople: Set<string>;
  token: string;
  isShareAccessInProgress: boolean;
}
interface Action {
  type: string;
  personUuid: string;
  token: string;
  isShareAccessInProgress: boolean;
}

const defaultAction: Action = {
  type: "",
  personUuid: "",
  token: "",
  isShareAccessInProgress: false
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case "addPerson":
      return {
        ...state,
        selectedPeople: state.selectedPeople.add(action.personUuid)
      };
    case "removePerson":
      state.selectedPeople.delete(action.personUuid);
      return {
        ...state,
        selectedPeople: state.selectedPeople
      };
    case "setToken":
      return { ...state, token: action.token };
    case "setIsShareAccessInProgress":
      return {
        ...state,
        isShareAccessInProgress: action.isShareAccessInProgress
      };

    default:
      return state;
  }
};

export function ShareAccessModal({
  toggleShareAccessModal,
  updatePeopleWithAccess,
  showShareAccessModal,
  availablePeople,
  modalData
}: ShareAccessModalProps) {
  let selectedPeopleSet = new Set<string>();

  modalData.selectedPeople.forEach((person: People) => {
    selectedPeopleSet.add(person.uuid);
  });

  const initialState: State = {
    selectedPeople: selectedPeopleSet,
    token: "",
    isShareAccessInProgress: false
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const modalSubHeading = useIntl().formatMessage({
    id: "app.academicsAndOffices.shareAccessModal.subHeading"
  });
  const modalHeading = useIntl().formatMessage(
    {
      id: "app.academicsAndOffices.shareAccessModal.heading"
    },
    { title: modalData.title }
  );

  const peopleWithAccess = useIntl().formatMessage({
    id: "app.academicsAndOffices.peopleWithAccess"
  });
  const placeholdar = useIntl().formatMessage({
    id: "app.academicsAndOffices.shareAccessModal.placeholdar"
  });
  const secondaryBtn = useIntl().formatMessage({
    id: "app.settings.reportingYearModal.secondaryBtn"
  });
  const primaryBtn = useIntl().formatMessage({
    id: "app.academicsAndOffices.shareAccessModal.primaryBtn"
  });
  const email = useIntl().formatMessage({
    id: "app.drilldown.email"
  });
  const name = useIntl().formatMessage({
    id: "app.academicsAndOffices.shareAccessModal.name"
  });
  const handleTagOptionSelected = (ev: any) => {
    dispatch({
      ...defaultAction,
      type: "addPerson",
      personUuid: ev.target["data-person-uuid"]
    });
    ev.target.selected = true;
  };
  const handleTagOptionDeselected = (ev: any) => {
    dispatch({
      ...defaultAction,
      type: "removePerson",
      personUuid: ev.target["data-person-uuid"]
    });
    if (ev.target.getAttribute("user-added")) {
      ev.target.remove();
    } else {
      ev.target.selected = false;
    }
  };

  const shareNodeAccess = () => {
    if (!state.isShareAccessInProgress) {
      dispatch({
        ...defaultAction,
        type: "setIsShareAccessInProgress",
        isShareAccessInProgress: true
      });
    }
  };

  useEffect(() => {
    if (state.isShareAccessInProgress) {
      const applyResult = (result: { token: string }) => {
        dispatch({
          ...defaultAction,
          type: "setToken",
          token: result.token
        });
      };
      sendRequest(
        {
          uri: "/export/token",
          camelize: true
        },
        applyResult
      );
    }
  }, [state.isShareAccessInProgress]);

  useEffect(() => {
    if (state.token !== "" && state.isShareAccessInProgress) {
      shareAccessToNode();
    }
  }, [state.token, state.isShareAccessInProgress]);

  const shareAccessToNode = () => {
    const type = modalData.nodeType === "program" ? "program" : "organization";

    let formData = new FormData();
    state.selectedPeople.forEach((personUuid) => {
      formData.append("person_uuids[]", personUuid);
    });

    formData.append("_csrf_token", state.token ? state.token : "");

    const applyResult = () => {
      toggleShareAccessModal();
      updatePeopleWithAccess(
        state.selectedPeople,
        modalData.nodeType,
        modalData.nodeUuid
      );
      dispatch({
        ...defaultAction,
        type: "setToken",
        token: ""
      });
      dispatch({
        ...defaultAction,
        type: "setIsShareAccessInProgress",
        isShareAccessInProgress: false
      });
    };

    sendRequestWithPayload(
      {
        uri: `/sa/${type}/${modalData.nodeUuid}/share_access`,
        body: formData
      },
      applyResult
    );
  };

  return (
    <>
      <WmModal
        open={showShareAccessModal}
        id="share-access-modal"
        elementToFocus="taginputtable"
        onWmModalCloseTriggered={() => toggleShareAccessModal()}
        onWmModalSecondaryTriggered={() => toggleShareAccessModal()}
        onWmModalPrimaryTriggered={() => shareNodeAccess()}
      >
        <WmModalHeader
          heading={modalHeading}
          subheading={modalSubHeading}
        ></WmModalHeader>

        <div id="content-share-access-modal">
          {state.isShareAccessInProgress ? (
            <div className="loader-wrapper">
              <WidgetLoader
                loaderContainerClasses="loader-container"
                loaderClasses="widget-loader"
              />
            </div>
          ) : (
            <WmTagInput
              id="taginputtable"
              tag-input-type="table"
              label={peopleWithAccess}
              col-headers={`${name},${email}`}
              col-widths="40%,60%"
              col-wrap="wrap,trunc,wrap"
              placeholder={placeholdar}
            >
              {availablePeople && (
                <RenderSelectedPeople
                  availablePeople={availablePeople}
                  handleSeleted={handleTagOptionSelected}
                  handleDeseleted={handleTagOptionDeselected}
                  selectedPeople={state.selectedPeople}
                />
              )}
            </WmTagInput>
          )}
        </div>
        <WmModalFooter
          primary-text={primaryBtn}
          secondary-text={secondaryBtn}
        ></WmModalFooter>
      </WmModal>
    </>
  );
}

interface RenderSelectedPeopleProps {
  availablePeople: People[] | null;
  handleSeleted: (event: any) => void;
  handleDeseleted: (event: any) => void;
  selectedPeople: Set<string>;
}
const RenderSelectedPeople = ({
  availablePeople,
  handleSeleted,
  handleDeseleted,
  selectedPeople
}: RenderSelectedPeopleProps) => {
  // Ensure availablePeople is an array before mapping
  if (!availablePeople || !Array.isArray(availablePeople)) {
    return null;
  }

  return (
    <>
      {availablePeople.map((user: People) => {
        return (
          <WmTagOption
            key={user.uuid}
            id={`person-${user.uuid}`}
            data-person-uuid={user.uuid}
            col1={getFullName(user)}
            col2={user.email}
            onWmTagOptionSelected={handleSeleted}
            onWmTagOptionDeselected={handleDeseleted}
            selected={selectedPeople?.has(user.uuid)}
          />
        );
      })}
    </>
  );
};
